import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RichTextEditor from "@/components/RichTextEditor";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { POSITIONS, CATEGORIES, BELTS } from "@/lib/constants";
import { Upload, Loader2, Link2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", position: "", category: "", belt_level: "", tags: [],
  video_type: "none", video_url: "", video_storage_path: "", thumbnail_storage_path: "", scope: "personal" };

export default function TechniqueForm({ open, onOpenChange, technique, onSaved }) {
  const { user } = useAuth();
  const isPlatform = user?.role === "platform_admin";
  const canGym = (user?.role === "admin" || user?.role === "coach") && user?.gym_id;
  const canUpload = user?.role === "admin" || user?.role === "coach" || isPlatform;
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [videoTab, setVideoTab] = useState("link");

  useEffect(() => {
    if (open) {
      setForm(technique ? { ...EMPTY, ...technique, tags: technique.tags || [] } : { ...EMPTY, scope: isPlatform ? "platform" : "personal" });
      setTagInput("");
      setVideoTab(technique?.video_type === "upload" ? "upload" : "link");
    }
  }, [open, technique, isPlatform]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, video_storage_path: data.storage_path, thumbnail_storage_path: data.thumbnail_path || "" }));
      setVideoTab("upload");
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Derive video_type from the active tab and actual content so it always saves correctly
      if (videoTab === "upload" && form.video_storage_path) {
        payload.video_type = "upload";
        payload.video_url = "";
      } else if (videoTab === "link" && form.video_url?.trim()) {
        payload.video_type = "link";
        payload.video_url = form.video_url.trim();
        payload.video_storage_path = "";
        payload.thumbnail_storage_path = "";
      } else {
        payload.video_type = "none";
        payload.video_url = "";
        payload.video_storage_path = "";
        payload.thumbnail_storage_path = "";
      }
      if (technique?.id) await api.put(`/techniques/${technique.id}`, payload);
      else await api.post("/techniques", payload);
      toast.success(technique ? "Technique updated" : "Technique added");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const showUpload = canUpload || form.video_type === "upload";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="technique-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{technique ? "Edit technique" : "Add technique"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input data-testid="technique-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
              className="mt-1.5 bg-background border-border" placeholder="Triangle Choke" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Position</Label>
              <Select value={form.position} onValueChange={(v) => set("position", v)}>
                <SelectTrigger data-testid="technique-position-select" className="mt-1.5 bg-background border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="technique-category-select" className="mt-1.5 bg-background border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Belt level</Label>
              <Select value={form.belt_level} onValueChange={(v) => set("belt_level", v)}>
                <SelectTrigger data-testid="technique-belt-select" className="mt-1.5 bg-background border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{BELTS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Visibility</Label>
              <Select value={form.scope} onValueChange={(v) => set("scope", v)}>
                <SelectTrigger data-testid="technique-scope-select" className="mt-1.5 bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isPlatform ? (
                    <SelectItem value="platform">🌐 Platform (everyone)</SelectItem>
                  ) : <>
                    <SelectItem value="personal">👤 Personal (private)</SelectItem>
                    {canGym && <SelectItem value="gym">🏫 Gym (shared)</SelectItem>}
                  </>}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
            <div className="mt-1.5">
              <RichTextEditor
                testid="technique-description-input"
                value={form.description}
                onChange={(html) => set("description", html)}
                placeholder="Key details, grips, common mistakes…  Paste from ChatGPT/Gemini keeps formatting."
                minHeight={140}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tags</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="technique-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                className="bg-background border-border" placeholder="Add tag + Enter" />
              <Button type="button" variant="outline" onClick={addTag} className="border-border">Add</Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.tags.map((t) => (
                  <span key={t} onClick={() => set("tags", form.tags.filter((x) => x !== t))}
                    className="cursor-pointer text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground hover:bg-primary/20 hover:text-primary">{t} ✕</span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Video</Label>
            {showUpload ? (
              <Tabs value={videoTab} onValueChange={setVideoTab} className="mt-1.5">
                <TabsList className="bg-background border border-border">
                  <TabsTrigger value="link" data-testid="video-tab-link"><Link2 className="h-3.5 w-3.5 mr-1.5" /> Link</TabsTrigger>
                  <TabsTrigger value="upload" data-testid="video-tab-upload"><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload</TabsTrigger>
                </TabsList>
                <TabsContent value="link" className="mt-3">
                  <Input data-testid="technique-video-url-input" value={form.video_url} onChange={(e) => set("video_url", e.target.value)}
                    className="bg-background border-border" placeholder="YouTube or Vimeo URL" />
                </TabsContent>
                <TabsContent value="upload" className="mt-3">
                  <label className="flex items-center justify-center gap-2 h-20 rounded-lg border border-dashed border-border bg-background cursor-pointer hover:border-primary/60 transition-colors">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> :
                      form.video_storage_path ? <span className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="h-4 w-4" /> Video ready — replace</span> :
                      <span className="flex items-center gap-2 text-muted-foreground text-sm"><Upload className="h-4 w-4" /> Click to upload a video file</span>}
                    <input data-testid="technique-video-upload-input" type="file" accept="video/*" className="hidden" onChange={onUpload} disabled={uploading} />
                  </label>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="mt-1.5">
                <Input data-testid="technique-video-url-input" value={form.video_url} onChange={(e) => set("video_url", e.target.value)}
                  className="bg-background border-border" placeholder="YouTube or Vimeo URL" />
                <p className="text-[11px] text-muted-foreground mt-1.5">Add a YouTube or Vimeo link. Video file uploads are available to gym admins and coaches.</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="technique-save-btn" onClick={save} disabled={saving || uploading}
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? "Saving…" : technique ? "Save changes" : "Add technique"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
